import { Component, EventEmitter, Output, inject, signal, computed, ElementRef, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { ThemeService } from '../../core/services/theme.service';
import { NotificationService } from '../../core/services/notification.service';
import { DesktopNotificationService } from '../../core/services/desktop-notification.service';
import { UserService } from '../../core/services/user.service';
import { DepartmentService } from '../../core/services/department.service';
import { AuthenticationService } from '../../core/services/Authentication/authentication.service';
import { PermissionService } from '../../core/services/Authentication/permission.service';
import { TicketService } from '../../core/services/ticket.service';
import { UserAvatarComponent } from '../../shared/components/avatar/user-avatar.component';
import { NotificationItem } from '../../core/models/notification.model';

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [CommonModule, UserAvatarComponent],
  template: `
    <header class="h-16 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 px-4 md:px-6 flex items-center justify-between sticky top-0 z-30 shadow-xs transition-colors duration-200">
      
      <!-- Left: Title & Quick Search Button -->
      <div class="flex items-center space-x-4">
        <button
          (click)="toggleSidebar.emit()"
          type="button"
          aria-label="Toggle Sidebar"
          title="Toggle Side Navigation Bar"
          class="p-2 rounded-xl text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors cursor-pointer">
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16"/>
          </svg>
        </button>

        <div>
          <h1 class="text-sm md:text-base font-extrabold text-slate-900 dark:text-white tracking-tight">Support Desk</h1>
          <p class="text-[10px] text-slate-500 dark:text-slate-400 font-mono hidden md:block">Internal Request System</p>
        </div>

        <!-- Global Search Trigger -->
        <button
          (click)="openSearch.emit()"
          type="button"
          class="hidden sm:flex items-center space-x-3 px-3 py-1.5 rounded-xl bg-slate-100 dark:bg-slate-800/80 border border-slate-200 dark:border-slate-700/60 text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-200 dark:hover:bg-slate-800 transition-colors text-xs">
          <svg class="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
          </svg>
          <span>Search tickets...</span>
          <kbd class="px-1.5 py-0.5 text-[10px] font-mono bg-white dark:bg-slate-900 rounded-md border border-slate-200 dark:border-slate-700 text-slate-500 dark:text-slate-400">Ctrl K</kbd>
        </button>
      </div>

      <!-- Right: Action Icons & Profile -->
      <div class="flex items-center space-x-2 md:space-x-3">

        <!-- Guest Quick Account Request Button -->
        @if (isGuestUser()) {
          <button
            (click)="ticketService.openGuestAccountRequestTicket()"
            type="button"
            title="Create a quick ticket to request a dedicated personal account"
            class="flex items-center space-x-1.5 px-3 py-1.5 rounded-xl bg-blue-600 hover:bg-blue-500 text-white font-bold text-xs shadow-md shadow-blue-600/25 transition-all cursor-pointer animate-in fade-in">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
            </svg>
            <span class="hidden sm:inline">Request Dedicated Account</span>
            <span class="sm:hidden">Request Account</span>
          </button>
        }

        <!-- Theme Toggle -->
        <button
          (click)="themeService.toggleTheme()"
          type="button"
          [title]="'Switch to ' + (themeService.currentTheme() === 'dark' ? 'Light' : 'Dark') + ' Mode'"
          class="p-2 rounded-xl text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors">
          @if (themeService.currentTheme() === 'dark') {
            <svg class="w-5 h-5 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z"/>
            </svg>
          } @else {
            <svg class="w-5 h-5 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z"/>
            </svg>
          }
        </button>

        <!-- Notification Bell & Dropdown -->
        <div class="relative">
          <button
            (click)="toggleNotifications()"
            type="button"
            class="p-2 rounded-xl text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors relative">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 01-6 0v-1m6 0H9"/>
            </svg>

            <!-- Unread Badge Indicator -->
            @if (unreadCount() > 0) {
              <span class="absolute -top-0.5 -right-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-mono font-bold bg-rose-600 text-white shadow-md shadow-rose-600/40 border border-white dark:border-slate-900 animate-pulse">
                {{ unreadCount() > 99 ? '99+' : unreadCount() }}
              </span>
            }
          </button>

          <!-- Notifications Dropdown Panel -->
          @if (showNotifications()) {
            <div class="absolute right-0 mt-2 w-80 md:w-96 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-2xl overflow-hidden z-50 animate-in fade-in slide-in-from-top-2">
              
              <!-- Panel Header -->
              <div class="px-4 py-3 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between bg-slate-50 dark:bg-slate-950/50">
                <div class="flex items-center space-x-2">
                  <h3 class="text-xs font-bold text-slate-900 dark:text-white uppercase tracking-wider">Notifications</h3>
                  @if (unreadCount() > 0) {
                    <span class="px-2 py-0.5 rounded-full bg-rose-500/10 text-rose-600 dark:text-rose-400 font-mono text-[10px] font-bold">
                      {{ unreadCount() }} unread
                    </span>
                  }
                </div>
                <button
                  (click)="notificationService.markAllAsRead()"
                  type="button"
                  class="text-[11px] font-semibold text-blue-600 dark:text-blue-400 hover:underline cursor-pointer">
                  Mark all read
                </button>
              </div>

              <!-- Desktop Permission Banner (if not yet decided) -->
              @if (desktopNotificationService.isSupported() && desktopNotificationService.permission() === 'default') {
                <div class="px-3.5 py-2.5 bg-blue-50/80 dark:bg-blue-950/40 border-b border-blue-100 dark:border-blue-900/50 flex items-center justify-between gap-2">
                  <div class="flex items-center space-x-2 min-w-0">
                    <svg class="w-4 h-4 text-blue-600 dark:text-blue-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 01-6 0v-1m6 0H9"/>
                    </svg>
                    <span class="text-[11px] font-medium text-blue-950 dark:text-blue-200 truncate">Enable desktop popups?</span>
                  </div>
                  <button
                    (click)="enableDesktopAlerts()"
                    type="button"
                    class="px-2.5 py-1 rounded-md bg-blue-600 hover:bg-blue-500 text-white text-[10px] font-bold shrink-0 transition-colors shadow-xs cursor-pointer">
                    Enable
                  </button>
                </div>
              }

              <!-- Filter Tabs -->
              <div class="flex items-center space-x-2 px-3 py-2 bg-slate-100/50 dark:bg-slate-900/50 border-b border-slate-200/60 dark:border-slate-800/60 text-[11px]">
                <button
                  (click)="filterUnreadOnly.set(false)"
                  type="button"
                  [ngClass]="!filterUnreadOnly() ? 'bg-white dark:bg-slate-800 text-blue-600 dark:text-blue-400 font-bold shadow-xs' : 'text-slate-500 hover:text-slate-900 dark:hover:text-white'"
                  class="px-2.5 py-1 rounded-lg transition-colors">
                  All
                </button>
                <button
                  (click)="filterUnreadOnly.set(true)"
                  type="button"
                  [ngClass]="filterUnreadOnly() ? 'bg-white dark:bg-slate-800 text-blue-600 dark:text-blue-400 font-bold shadow-xs' : 'text-slate-500 hover:text-slate-900 dark:hover:text-white'"
                  class="px-2.5 py-1 rounded-lg transition-colors">
                  Unread Only ({{ unreadCount() }})
                </button>
              </div>

              <!-- Scrollable List -->
              <div class="max-h-80 overflow-y-auto divide-y divide-slate-100 dark:divide-slate-800/60 p-1">
                @for (item of displayedNotifications(); track item.id) {
                  <div
                    (click)="onNotificationClick(item)"
                    [ngClass]="!item.read ? 'bg-blue-50/60 dark:bg-blue-950/30 font-medium' : ''"
                    class="p-3 hover:bg-slate-100 dark:hover:bg-slate-800/60 rounded-xl transition-colors cursor-pointer space-y-1 group relative">
                    
                    <div class="flex items-center justify-between">
                      <span class="text-xs font-bold text-slate-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 flex items-center space-x-1.5">
                        @if (!item.read) {
                          <span class="w-2 h-2 rounded-full bg-blue-600 shrink-0"></span>
                        }
                        <span>{{ item.title }}</span>
                      </span>
                      <span class="text-[10px] text-slate-400 dark:text-slate-500 font-mono shrink-0 ml-2">{{ item.timestamp }}</span>
                    </div>

                    <p class="text-xs text-slate-600 dark:text-slate-300 leading-relaxed line-clamp-2 pl-3.5">{{ item.message }}</p>

                    @if (item.ticketId) {
                      <div class="pl-3.5 pt-1 flex items-center space-x-1 text-[10px] text-blue-600 dark:text-blue-400 font-mono font-bold">
                        <span>View Ticket Details &rarr;</span>
                      </div>
                    }
                  </div>
                } @empty {
                  <div class="py-8 text-center text-slate-400 dark:text-slate-500 font-mono text-xs">
                    {{ filterUnreadOnly() ? 'No unread notifications!' : 'No notifications yet.' }}
                  </div>
                }
              </div>

            </div>
          }
        </div>

        <!-- User Profile Dropdown -->
        <div class="relative">
          <button
            (click)="toggleProfileMenu()"
            type="button"
            class="flex items-center space-x-2 pl-2 border-l border-slate-200 dark:border-slate-800 hover:opacity-80 transition-opacity cursor-pointer focus:outline-none">
            <app-user-avatar [name]="currentUser().name" [src]="currentUser().avatarUrl" size="sm" />
            <div class="hidden lg:block text-left">
              <p class="text-xs font-bold text-slate-900 dark:text-white leading-none">{{ currentUser().name }}</p>
              @if (currentDeptName()) {
                <p class="text-[10px] text-blue-500 dark:text-blue-400 font-mono mt-0.5">{{ currentDeptName() }}</p>
              } @else {
                <p class="text-[10px] text-slate-500 dark:text-slate-400 font-mono mt-0.5">{{ currentUser().role }}</p>
              }
            </div>
            <svg class="w-3.5 h-3.5 text-slate-400 ml-1 hidden lg:block" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/>
            </svg>
          </button>

          <!-- Profile Dropdown Menu -->
          @if (showProfileMenu()) {
            <div class="absolute right-0 mt-2 w-64 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-2xl overflow-hidden z-50 p-2 animate-in fade-in slide-in-from-top-2">
              <div class="p-3 border-b border-slate-100 dark:border-slate-800/80 bg-slate-50/50 dark:bg-slate-950/40 rounded-xl mb-1">
                <p class="text-xs font-bold text-slate-900 dark:text-white truncate">{{ currentUser().name }}</p>
                <p class="text-[11px] text-slate-500 dark:text-slate-400 truncate">{{ currentUser().email }}</p>
                <div class="mt-2 flex items-center flex-wrap gap-1">
                  <span class="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-semibold bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-300">
                    {{ currentUser().role }}
                  </span>
                  @if (currentDeptName()) {
                    <span class="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-semibold bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300">
                      <svg class="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-2 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"/>
                      </svg>
                      {{ currentDeptName() }}
                    </span>
                  }
                </div>
              </div>

              @if (isGuestUser()) {
                <button
                  (click)="openAccountRequestFromMenu()"
                  type="button"
                  class="w-full flex items-center space-x-2.5 px-3 py-2.5 rounded-xl text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-950/40 transition-colors text-xs font-bold cursor-pointer">
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                  </svg>
                  <span>Request Dedicated Account</span>
                </button>
              }

              <button
                (click)="logout()"
                type="button"
                class="w-full flex items-center space-x-2.5 px-3 py-2.5 rounded-xl text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-950/40 transition-colors text-xs font-bold cursor-pointer">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"/>
                </svg>
                <span>Logout to Auth Portal</span>
              </button>
            </div>
          }
        </div>

      </div>

    </header>
  `,
})
export class HeaderComponent {
  private readonly router = inject(Router);
  private readonly elementRef = inject(ElementRef);
  readonly themeService = inject(ThemeService);
  readonly notificationService = inject(NotificationService);
  readonly desktopNotificationService = inject(DesktopNotificationService);
  readonly userService = inject(UserService);
  readonly authService = inject(AuthenticationService);
  readonly ticketService = inject(TicketService);
  private readonly permissionService = inject(PermissionService);
  private readonly departmentService = inject(DepartmentService);

  readonly currentUser = this.userService.currentUser;
  readonly notifications = this.notificationService.notifications;
  readonly unreadCount = this.notificationService.unreadCount;

  readonly isGuestUser = computed(() => {
    const user = this.authService.currentUser();
    return user?.username === 'guest' || user?.name === 'Guest User' || String(user?.id) === '1';
  });

  /** Resolved department name — shown for EMPLOYEE and DEPARTMENT_HEAD */
  readonly currentDeptName = computed(() => {
    if (this.permissionService.isGlobalAdmin()) return '';
    const user = this.authService.currentUser();
    const deptId = user?.departmentId;
    if (!deptId) return '';
    const dept = this.departmentService.departments().find((d) => String(d.id) === String(deptId));
    return dept?.name || '';
  });

  readonly showNotifications = signal(false);
  readonly showProfileMenu = signal(false);
  readonly filterUnreadOnly = signal(false);

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.showNotifications.set(false);
      this.showProfileMenu.set(false);
    }
  }

  readonly displayedNotifications = computed(() => {
    const list = this.notifications();
    if (this.filterUnreadOnly()) {
      return list.filter((item) => !item.read);
    }
    return list;
  });

  @Output() toggleSidebar = new EventEmitter<void>();
  @Output() openSearch = new EventEmitter<void>();

  toggleNotifications(): void {
    this.showNotifications.update((v) => !v);
    if (this.showNotifications()) {
      this.showProfileMenu.set(false);
      this.notificationService.loadNotifications().subscribe();
    }
  }

  async enableDesktopAlerts(): Promise<void> {
    await this.desktopNotificationService.requestPermission();
  }

  toggleProfileMenu(): void {
    this.showProfileMenu.update((v) => !v);
    if (this.showProfileMenu()) {
      this.showNotifications.set(false);
    }
  }

  onNotificationClick(item: NotificationItem): void {
    this.notificationService.markAsRead(item.id);
    this.showNotifications.set(false);

    if (item.ticketId) {
      this.router.navigate(['/tickets/view', item.ticketId]);
    }
  }

  openAccountRequestFromMenu(): void {
    this.showProfileMenu.set(false);
    this.ticketService.openGuestAccountRequestTicket();
  }

  logout(): void {
    this.showProfileMenu.set(false);
    this.authService.logout();
  }
}
